import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Heart, User, ShoppingBag, Menu, X } from "lucide-react";
import { useStore } from "../context/StoreContext";
import { useAuth } from "../context/AuthContext";
import SearchModal from "./SearchModal";
import { Sheet, SheetContent, SheetTitle } from "./ui/sheet";

const NAV = [
  { label: "Shop", to: "/shop" },
  { label: "New Arrivals", to: "/shop?new_arrival=true" },
  { label: "Handbags", to: "/shop?category=Handbags" },
  { label: "Accessories", to: "/shop?category=Accessories" },
  { label: "Brands", to: "/brands" },
  { label: "About", to: "/about" },
  { label: "Authentication", to: "/authentication" },
  { label: "Contact", to: "/contact" },
];

export default function Navbar() {
  const { cart, setCartOpen, wishlist } = useStore();
  const { user } = useAuth();
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <div className="bg-bone border-b border-linen text-center py-1.5 text-[11px] font-mono tracking-[0.18em] text-charcoal/70 px-4">
        ARCHIVAL AUTHENTICATION · INSURED AUSTRALIA-WIDE DELIVERY · ONE-OF-ONE PIECES
      </div>
      <header className={`sticky top-0 z-50 w-full bg-ivory/95 backdrop-blur-md border-b transition-colors ${scrolled ? "border-linen" : "border-transparent"}`}>
        <div className="luxy-container">
          <div className="flex items-center justify-between h-16 lg:h-20 gap-4">
            {/* Left: mobile menu + desktop links */}
            <div className="flex items-center gap-2 flex-1">
              <button className="lg:hidden p-2 -ml-2" onClick={() => setMobileOpen(true)}
                aria-label="Open menu" data-testid="mobile-menu-trigger">
                <Menu className="w-5 h-5" />
              </button>
              <nav className="hidden lg:flex items-center gap-6">
                {NAV.slice(0, 4).map((n) => (
                  <Link key={n.label} to={n.to} data-testid={`nav-link-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                    className="text-[12px] uppercase tracking-[0.14em] text-charcoal hover:text-champagne-dark transition-colors">
                    {n.label}
                  </Link>
                ))}
              </nav>
            </div>

            {/* Center: wordmark */}
            <Link to="/" data-testid="brand-logo"
              className="font-serif text-2xl lg:text-3xl tracking-[0.3em] text-pitch shrink-0 pl-2">
              LUXYREIGN
            </Link>

            {/* Right: links + icons */}
            <div className="flex items-center gap-4 flex-1 justify-end">
              <nav className="hidden lg:flex items-center gap-6 mr-2">
                {NAV.slice(4).map((n) => (
                  <Link key={n.label} to={n.to} data-testid={`nav-link-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                    className="text-[12px] uppercase tracking-[0.14em] text-charcoal hover:text-champagne-dark transition-colors">
                    {n.label}
                  </Link>
                ))}
              </nav>
              <button onClick={() => setSearchOpen(true)} aria-label="Search" data-testid="nav-search-trigger"
                className="p-1.5 hover:text-champagne-dark transition-colors"><Search className="w-[18px] h-[18px]" /></button>
              <Link to="/wishlist" aria-label="Wishlist" data-testid="nav-wishlist-trigger"
                className="p-1.5 relative hover:text-champagne-dark transition-colors">
                <Heart className="w-[18px] h-[18px]" />
                {wishlist.length > 0 && <span className="absolute -top-0.5 -right-0.5 bg-pitch text-ivory text-[9px] rounded-full w-4 h-4 flex items-center justify-center font-mono">{wishlist.length}</span>}
              </Link>
              <Link to={user && user !== false ? (user.role === "admin" ? "/admin" : "/account") : "/login"}
                aria-label="Account" data-testid="nav-account-trigger"
                className="p-1.5 hover:text-champagne-dark transition-colors"><User className="w-[18px] h-[18px]" /></Link>
              <button onClick={() => setCartOpen(true)} aria-label="Shopping bag" data-testid="nav-cart-trigger"
                className="p-1.5 relative hover:text-champagne-dark transition-colors">
                <ShoppingBag className="w-[18px] h-[18px]" />
                {cart.length > 0 && <span className="absolute -top-0.5 -right-0.5 bg-pitch text-ivory text-[9px] rounded-full w-4 h-4 flex items-center justify-center font-mono">{cart.length}</span>}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="bg-ivory w-[85vw] sm:w-[380px] border-linen p-0">
          <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
          <div className="flex items-center justify-between px-6 h-16 border-b border-linen">
            <span className="font-serif text-xl tracking-[0.3em]">LUXYREIGN</span>
            <button onClick={() => setMobileOpen(false)} aria-label="Close"><X className="w-5 h-5" /></button>
          </div>
          <nav className="flex flex-col p-6 gap-1">
            {NAV.map((n) => (
              <Link key={n.label} to={n.to} onClick={() => setMobileOpen(false)}
                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                className="font-serif text-2xl py-2 text-charcoal hover:text-champagne-dark transition-colors">
                {n.label}
              </Link>
            ))}
            <Link to="/wishlist" onClick={() => setMobileOpen(false)} className="font-serif text-2xl py-2">Wishlist</Link>
            <Link to={user && user !== false ? "/account" : "/login"} onClick={() => setMobileOpen(false)} className="font-serif text-2xl py-2">Account</Link>
          </nav>
        </SheetContent>
      </Sheet>

      <SearchModal open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
}
